import Skeleton from "@/components/atoms/Skeleton";
import { ProductDetails } from "@/lib/centra/formatters";
import type {
	AlgoliaProductCard,
	ProductCard as ProductCardType,
} from "@raumagarn/formatters";
import config from "./config";
import { GallerySkeleton } from "./parts/Gallery/Gallery";
import {
	Colors,
	WeightAndLength,
	YarnWidget,
} from "./parts/YarnWidget/YarnWidget";
import PC from "./ProductCardTemplate";

const { Card, Gallery, Content, Description, Price, Name, Tags, Inline } = PC;

export interface ProductCardProps {
	productCard:
		| Omit<ProductCardType, "relatedProducts" | "variants">
		| Omit<AlgoliaProductCard, "relatedProducts" | "variants">
		| Omit<ProductDetails, "relatedProducts" | "variants">;
}

export const ProductCard = ({ productCard }: ProductCardProps) => {
	if (!productCard.productType) return null;

	const {
		name,
		uri,
		gallery,
		sku,
		fits,
		yarnType,
		yarnLengthAndWeight,
		yarnOptionLength,
		yarnMaterials,
		cannonicalCategory,
		media,
	} = productCard;

	const productTypeSlug =
		config.slugs[productCard.productType as keyof typeof config.slugs];

	const image = gallery?.image || media?.[0];
	const secondImage = gallery?.secondImage || media?.[1];

	return (
		<Card
			name={name ?? ""}
			slug={productTypeSlug ? `${productTypeSlug}${uri}` : "#"}
		>
			<Gallery
				url={image?.href ?? ""}
				alt={image?.alt ?? ""}
				secondImage={secondImage}
			>
				<Tags activeTag={gallery?.activeTag} />
			</Gallery>
			<Content>
				{productCard.productType === "accessory" && (
					<>
						<Description>
							<Name>{name ?? ""}</Name>
						</Description>

						<Price pricelist={productCard.pricelist} />
					</>
				)}
				{productCard.productType === "recipe" && (
					<>
						<Description>
							<Inline>
								{sku && <strong>{sku}</strong>}
								{fits && <span>{fits}</span>}
							</Inline>

							{name && <Name>{name}</Name>}

							<Inline>{yarnType && <strong>{yarnType}</strong>}</Inline>
						</Description>

						<Inline>
							<Price pricelist={productCard.pricelist} />
						</Inline>
					</>
				)}
				{productCard.productType === "yarn" && (
					<>
						<Description>
							{name && <Name>{name}</Name>}

							<YarnWidget>
								<WeightAndLength>{yarnLengthAndWeight}</WeightAndLength>
								<Colors
									total={yarnOptionLength ?? 0}
									swatchUrl={secondImage?.href ?? image?.href}
								/>
							</YarnWidget>

							<span>{yarnMaterials}</span>
						</Description>
						<Price pricelist={productCard.pricelist} />
					</>
				)}
				{productCard.productType === "booklet" && (
					<>
						<Description>
							<Inline>
								<strong>{sku}</strong>
								<span>{cannonicalCategory}</span>
							</Inline>
							<Name>{name}</Name>
							<strong>{yarnType}</strong>
						</Description>
						<Price pricelist={productCard.pricelist} />
					</>
				)}
			</Content>
		</Card>
	);
};

export const ProductCardSkeleton = () => {
	return (
		<Card name={""} slug={"#"}>
			<GallerySkeleton />
			<Content>
				<Description>
					<Skeleton height="1.5rem" />

					<YarnWidget>
						<Skeleton height="1.25rem" />
						<Skeleton height="1.25rem" />
					</YarnWidget>

					<Skeleton height="1rem" />
				</Description>
			</Content>
		</Card>
	);
};
